const sectionsToRender = [
  {
    id: '#CartDrawer-ShippingBar',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-ShippingBar',
  },
  {
    id: '#CartDrawer-Items',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Items',
  },
  {
    id: '#CartDrawer-Recommendations',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Recommendations',
  },
  {
    id: '#CartDrawer-Summary',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Summary',
  },
  {
    id: '#cart-counter-drawer',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #cart-counter-drawer',
  },
];

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.inner = document.querySelector('.cart-drawer__inner');
    this.errorPopup = document.querySelector('error-popup');
    this.checkoutBtn = this.querySelector('[name="checkout"]');
    this.isFirstOpen = true;

    this.addEventListener('click', (e) => {
      const checkoutBtn = e.target.closest('[name="checkout"]');

      if (!checkoutBtn) return;

      const openedGiftcards = Array.from(
        this.querySelectorAll('cart-giftcard.is-open'),
      );
      // A message typed into an add-on card that was never added, or an edit to
      // an existing card message that was never confirmed with "Done".
      const pendingAdd = openedGiftcards.some(
        (giftcard) =>
          !giftcard.classList.contains('is-filled') &&
          giftcard.querySelector('[data-message-input]').value,
      );
      const pendingEdit = openedGiftcards.some(
        (giftcard) =>
          giftcard.classList.contains('is-filled') &&
          giftcard.querySelector('[data-message-input]').value &&
          giftcard.matchesInitialMessage?.() === false,
      );

      if (!pendingAdd && !pendingEdit) return;

      e.preventDefault();
      this.errorPopup.showPopup(
        pendingAdd
          ? this.dataset.giftcardAddError
          : this.dataset.giftcardEditError,
        4.5,
      );
    });

    this.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close(),
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.setCartLinks();
  }

  setCartLinks() {
    const cartLinks = document.querySelectorAll('[data-cart-link]');

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() !== 'SPACE') return;
        event.preventDefault();
        this.open(cartLink);
      });
    });
  }

  open(opener) {
    if (this.isFirstOpen) this.performRecommendations();

    this.isFirstOpen = false;

    const wasVisible = this.classList.contains('is-visible');

    if (opener) this.setActiveElement(opener);
    this.classList.add('is-visible');

    if (!wasVisible) {
      this.dispatchEvent(
        new CustomEvent('cart-drawer:open', { bubbles: true }),
      );
    }
    this.addEventListener(
      'transitionend',
      () => {
        this.focusOnCartDrawer();
      },
      { once: true },
    );
    bodyScroll.lock(this.inner);
  }

  close() {
    this.classList.remove('is-visible');
    this.pendingFocusElement = null;
    removeTrapFocus(this.activeElement);
    bodyScroll.unlock(this.inner);
    this.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  focusOnCartDrawer() {
    const containerToTrapFocusOn = this.querySelector('#CartDrawer');
    // A message field expanded by renderContents wins over the close button, so
    // the drawer transition doesn't steal focus back from the textarea.
    const focusElement = this.pendingFocusElement?.isConnected
      ? this.pendingFocusElement
      : this.querySelector('[data-drawer-close]');
    this.pendingFocusElement = null;
    trapFocus(containerToTrapFocusOn, focusElement);
    this.resetScrollPosition();
  }

  /**
   * Render the cart sections and open the drawer.
   *
   * @param {Object} response Cart response containing the rendered sections.
   * @param {Object} options
   * @param {String} options.expandMessageForKey Line item key whose message
   * field should be expanded and focused once rendered.
   * @returns {Void}
   */
  renderContents(response, options = {}) {
    this.scrollPosition = this.inner.scrollTop;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.querySelector(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        response.sections[section.section],
        section.selector,
      );
    });
    this.resetScrollPosition();
    this.expandMessage(options.expandMessageForKey);
    this.open();
  }

  /**
   * Expand and focus the message field of a single line item. No-op unless the
   * line item owns its message (message block product), so the linked add-on
   * greeting card of a regular product is never opened automatically.
   *
   * @param {String} lineItemKey
   * @returns {Void}
   */
  expandMessage(lineItemKey) {
    if (!lineItemKey) return;

    const giftcard = this.querySelector(
      `[data-line-item-key="${lineItemKey}"] cart-giftcard[data-own-message]`,
    );

    if (!giftcard) return;

    customElements.upgrade(giftcard);

    if (typeof giftcard.toggleOpenState !== 'function') return;

    const wasVisible = this.classList.contains('is-visible');

    giftcard.toggleOpenState(true);
    giftcard.scrollIntoView({ block: 'nearest' });
    // Keep the stored position in sync, otherwise resetScrollPosition scrolls
    // the expanded field back out of view.
    this.scrollPosition = this.inner.scrollTop;

    // Drawer was closed, so a transition is pending: hand the textarea to
    // focusOnCartDrawer, which runs on transitionend.
    if (!wasVisible) this.pendingFocusElement = giftcard.messageInput;
  }

  performRecommendations() {
    const recommendationsHolder = this.querySelector(
      '[data-cart-drawer-recommendations]',
    );

    if (!recommendationsHolder) {
      this.querySelector('cart-recommendations').slider.update();

      return;
    }

    fetch(recommendationsHolder.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const recommendations = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-cart-drawer-recommendations]').outerHTML;
        recommendationsHolder.outerHTML = recommendations;
      });
  }

  getSectionsToRender() {
    return [...Cart.getLiveRegions(), ...sectionsToRender];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  resetScrollPosition() {
    this.inner.scrollTop = this.scrollPosition;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.cartDrawer = document.querySelector('cart-drawer');

    if (!this.cartDrawer.isFirstOpen) {
      this.cartDrawer.performRecommendations();
    }
  }

  getSectionsToRender() {
    return [...Cart.getLiveRegions(), ...sectionsToRender];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
